import React, { ReactElement, useState, useEffect, useRef, FormEvent } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { FieldInputProps } from 'formik';

interface IChildFormControlState {
    value: any;
    disabled: boolean;
    isInvalid: boolean;
}
export interface IChildFormControlProps<T> {
    id: string;
    type?: string;
    textarea?: boolean;
    rows?: number;
    disabled?: boolean;
    metaErrorStatus: boolean;
    value?: any;
    placeholder?: string;
    labelCol?: number;
    estPetit?: boolean;
    appendedChildren?: ReactElement;
    inputFieldProps?: FieldInputProps<T>;
    emailUnique?: boolean | undefined;
    noAutoAjust?: boolean;
    notifyChange?: (newValue: any) => void;
    validateOnChangeCallback?: (value: any) => boolean; //must finish with 'callback' because there is already a prop named 'validateOnChange', but as a boolean not a function
}

export default function ChildFormControl<T>(props: IChildFormControlProps<T>): ReactElement {
    const {
        type,
        textarea,
        rows,
        disabled,
        metaErrorStatus,
        inputFieldProps,
        value,
        placeholder,
        estPetit,
        appendedChildren,
        notifyChange,
        validateOnChangeCallback,
        emailUnique,
        noAutoAjust,
    } = props;
    let isInvalid: boolean;

    if (emailUnique) {
        isInvalid = true;
    } else if (!disabled && validateOnChangeCallback) {
        isInvalid = metaErrorStatus || !validateOnChangeCallback(value);
    } else {
        isInvalid = metaErrorStatus;
    }
    const [state, setState] = useState<IChildFormControlState>({
        value: value,
        disabled: disabled,
        isInvalid: isInvalid,
    } as IChildFormControlState);

    const [cursor, setCursor] = useState(0);
    const ref = useRef(document.createElement('textarea'));

    useEffect(() => {
        setState((prev) => ({ ...prev, isInvalid: isInvalid }));
    }, [isInvalid]);

    useEffect(() => {
        if (ref === null) return;

        if (!noAutoAjust) autoAjusterZoneTexte(!!textarea, ref.current);
    }, [ref]);

    useEffect(() => {
        if (cursor) {
            ref.current.setSelectionRange(cursor, cursor);
        }
    }, [cursor, textarea]);

    const onChangeHandler = function (event) {
        setCursor(event.target.selectionStart);

        if (inputFieldProps?.onChange) {
            inputFieldProps.onChange(event);
        }

        const valueFromEvent: any = event.target.value;
        if (notifyChange) {
            notifyChange(valueFromEvent);
        }

        let validStatus = true;
        if (!state.disabled && validateOnChangeCallback) {
            validStatus = validateOnChangeCallback(valueFromEvent);
        }

        setState({
            ...state,
            value: valueFromEvent,
        } as IChildFormControlState);
    };
    const formControl: ReactElement = (
        <Form.Control
            {...inputFieldProps}
            type={type}
            as={textarea ? 'textarea' : undefined}
            rows={textarea ? rows : undefined}
            isInvalid={state.isInvalid}
            disabled={state.disabled}
            value={value ? value : ''}
            onChange={onChangeHandler}
            ref={ref}
            size={estPetit ? 'sm' : undefined}
            placeholder={placeholder}
        />
    );

    return appendedChildren ? (
        <InputGroup className="mb-3">
            {formControl}
            <InputGroup.Append>{appendedChildren}</InputGroup.Append>
        </InputGroup>
    ) : (
        formControl
    );
}

/**
 * Fonction utilitaire. Ajuste automatiquement la hauteur d'un Form.Control textarea
 * jusqu'a maxHeight.
 * @param estZoneTexte true si Form.Control est un textarea de html
 * @param zoneTexte le textarea qui a soulevé l'évenement
 * @returns void
 */
function autoAjusterZoneTexte(estZoneTexte: boolean, zoneTexte: HTMLTextAreaElement | null): void {
    if (!estZoneTexte || !zoneTexte) return;

    zoneTexte.style.height = 'auto';
    zoneTexte.style.height = `${zoneTexte.scrollHeight + 2}px`;
    zoneTexte.style.minHeight = `${zoneTexte.scrollHeight}px`;

    if (zoneTexte.scrollHeight > 150) {
        zoneTexte.style.height = '130px';
        zoneTexte.style.minHeight = '100px';
    }
}
